import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type {
  DeleteIntegrationBusinessUnitsUuid200,
  IntegrationValidationError
} from 'api';
import {
  getGetIntegrationBusinessUnitsQueryKey,
  useDeleteIntegrationBusinessUnitsUuid
} from 'api';

import { useOrganisationId } from 'hooks/context';

import {
  DELETE_BUSINESS_UNIT_GENERIC_ERROR,
  DELETE_BUSINESS_UNIT_SUCCESS
} from './constants';

import { extractIntegrationValidationError } from '../../utils/extractIntegrationValidationError';
import { useBusinessUnitsListSync } from '../BusinessUnitsListSyncContext';

export const useDeleteBusinessUnit = () => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const { onBusinessUnitDeleted } = useBusinessUnitsListSync();
  const deleteMutation = useDeleteIntegrationBusinessUnitsUuid();
  // `mutation.isPending` is stale within the click's render, so a same-tick
  // double click would pass it — the ref blocks the second DELETE.
  const inFlightRef = useRef(false);

  const deleteBusinessUnit = (uuid: string, onSuccess: () => void) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    deleteMutation.mutate(
      { organisationId, uuid },
      {
        onSettled: () => {
          inFlightRef.current = false;
        },
        onSuccess: (response: DeleteIntegrationBusinessUnitsUuid200) => {
          queryClient.invalidateQueries({
            queryKey: getGetIntegrationBusinessUnitsQueryKey(organisationId)
          });
          onBusinessUnitDeleted(response.data);
          openNotification({
            kind: 'success',
            message: DELETE_BUSINESS_UNIT_SUCCESS
          });
          onSuccess();
        },
        onError: (error: IntegrationValidationError | null) =>
          openNotification({
            kind: 'error',
            message: extractIntegrationValidationError(
              error,
              DELETE_BUSINESS_UNIT_GENERIC_ERROR
            )
          })
      }
    );
  };

  return { deleteBusinessUnit, isPending: deleteMutation.isPending };
};
